/**
 * ============================================================================
 * DEMAND CATEGORY REGISTRY — single source of truth for the WBR taxonomy.
 * ============================================================================
 *
 * Terminology and names match the EXISTING WBR QuickSight dashboard
 * (CollectionTrackerServiceCDK quicksightStack.ts `DEMAND_CATEGORIES`, backed
 * by the `demand_category` column). We do not invent new vocabulary.
 *
 * Adding/renaming an entry = editing THIS array only; the checkbox row,
 * display names, defaults, and data matching all derive from it. The
 * registry-coverage unit test keeps it in sync with the NetworkId slug type
 * (the slugs are pre-existing URL params — /network/:id — kept for link
 * stability).
 *
 * `values`: the DcrRecord.demandCategory values an entry matches. A list,
 * because summary entries in CTS are ROLLUPS over several demand categories.
 * TODO(data): confirm rollup composition for Nova Network / Alexa Network
 * against the CTS mapping once real datashare data lands.
 *
 * When the GetWBR config API ships, this file is the seam that
 * becomes API-driven.
 */

import { NetworkId } from 'src/types';

export interface DemandCategoryDefinition {
  /** URL-facing slug (pre-existing route param), enforced against NetworkId. */
  id: NetworkId;
  /** Display name exactly as in the WBR dashboard. */
  displayName: string;
  /** DcrRecord.demandCategory values that belong to this entry. */
  values: string[];
  /** Checked by default on the Detailed Summary tab (QuickSight default). */
  defaultSelected: boolean;
}

export const DEMAND_CATEGORIES: DemandCategoryDefinition[] = [
  { id: 'nova-network',  displayName: 'Nova Network',  values: ['Nova Network'],      defaultSelected: true },
  { id: 'nova-general',  displayName: 'Nova General',  values: ['Nova General'],      defaultSelected: true },
  { id: 'nova-expert',   displayName: 'Nova Expert',   values: ['Nova Expert'],       defaultSelected: true },
  { id: 'alexa-network', displayName: 'Alexa Network', values: ['Alexa Network'],     defaultSelected: true },
  { id: 'alexa-plus',    displayName: 'Alexa+',        values: ['Alexa+', 'Alexa +'], defaultSelected: true },
  { id: 'others',        displayName: 'Others',        values: ['Others'],            defaultSelected: false },
  { id: 'offline-brot',  displayName: 'Offline/BYOT',  values: ['Offline/BYOT'],      defaultSelected: false },
];

/** Ordered ids for the checkbox row (registry order = QuickSight order). */
export const DEMAND_CATEGORY_IDS: NetworkId[] = DEMAND_CATEGORIES.map((d) => d.id);

/** QuickSight default: the five main entries checked. */
export const DEFAULT_SELECTED_DEMAND_CATEGORIES: NetworkId[] = DEMAND_CATEGORIES.filter(
  (d) => d.defaultSelected,
).map((d) => d.id);

/** slug → display name (incl. the 'all' pseudo-entry). */
export const DEMAND_CATEGORY_DISPLAY_NAMES: Record<string, string> = {
  all: 'All Networks',
  ...Object.fromEntries(DEMAND_CATEGORIES.map((d) => [d.id, d.displayName])),
};

export function demandCategoryDisplayName(id: string): string {
  return DEMAND_CATEGORY_DISPLAY_NAMES[id] ?? id;
}

/** Does this DCR demandCategory value belong to the given entry? */
export function matchesDemandCategory(id: string, value: string | undefined): boolean {
  if (id === 'all') return true;
  const entry = DEMAND_CATEGORIES.find((d) => d.id === id);
  if (!entry) return false;
  return entry.values.includes(value ?? '');
}

/** demandCategory value → owning entry id (first match; undefined = unmapped). */
export function demandCategoryIdForValue(value: string | undefined): string | undefined {
  return DEMAND_CATEGORIES.find((d) => d.values.includes(value ?? ''))?.id;
}
